/**
 * WeatherMicroTile — square weather tile for the Welcome Book home grid.
 *
 * Renders like the other TileButtons (rounded 26px, gradient background)
 * but the gradient is picked from the current WMO code so it visually
 * reflects the weather (sunny/orange, rainy/blue, night/indigo, etc).
 * Displays big temperature + small weather glyph + label. On tap, calls
 * onClick so the parent can open a bottom sheet with the full forecast.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import AnimatedWeatherIcon from "./AnimatedWeatherIcon";

// Same WMO mapping used by WeatherWidget, kept in sync visually. Gradient
// colours are tuned to sit next to the other tiles in the grid without
// looking out of place (same 60% saturation). Icons are now rendered by
// AnimatedWeatherIcon based on WMO code + wind speed (flat + framer-motion).
const codeMap = (code) => {
  const c = Number(code);
  if (c === 0)  return { label: "Sereno",       gradient: "linear-gradient(135deg,#FDE68A 0%,#F59E0B 60%,#B45309 100%)" };
  if ([1, 2].includes(c)) return { label: "Poco nuvoloso", gradient: "linear-gradient(135deg,#93C5FD 0%,#3B82F6 60%,#1D4ED8 100%)" };
  if (c === 3)  return { label: "Nuvoloso",     gradient: "linear-gradient(135deg,#94A3B8 0%,#64748B 60%,#334155 100%)" };
  if ([45, 48].includes(c)) return { label: "Nebbia",  gradient: "linear-gradient(135deg,#CBD5E1 0%,#94A3B8 60%,#475569 100%)" };
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(c)) return { label: "Pioggia", gradient: "linear-gradient(135deg,#60A5FA 0%,#2563EB 60%,#1E3A8A 100%)" };
  if ([71, 73, 75, 77, 85, 86].includes(c))             return { label: "Neve",    gradient: "linear-gradient(135deg,#E0F2FE 0%,#7DD3FC 60%,#0284C7 100%)" };
  if ([95, 96, 99].includes(c))                          return { label: "Temporale", gradient: "linear-gradient(135deg,#818CF8 0%,#6D28D9 60%,#3730A3 100%)" };
  return { label: "Nuvoloso", gradient: "linear-gradient(135deg,#94A3B8 0%,#64748B 60%,#334155 100%)" };
};

const LABELS = {
  it: { now: "Oggi",       tap: "Previsioni 7 giorni" },
  en: { now: "Today",      tap: "7-day forecast" },
  fr: { now: "Aujourd'hui",tap: "Prévisions 7 jours" },
  es: { now: "Hoy",        tap: "Pronóstico 7 días" },
  de: { now: "Heute",      tap: "7-Tage-Vorhersage" },
};

export default function WeatherMicroTile({ lat, lng, lang = "it", onClick, testId = "tile-weather" }) {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const t = LABELS[lang] || LABELS.it;

  useEffect(() => {
    if (!lat || !lng) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min&timezone=auto&forecast_days=1`;
        const res = await fetch(url);
        const json = await res.json();
        if (!cancelled) setW(json);
      } catch {
        if (!cancelled) setW(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lng]);

  if (!lat || !lng) return null;

  // While loading, render an empty pill so the grid doesn't reflow.
  if (loading || !w?.current) {
    return (
      <div
        className="col-span-1 aspect-square rounded-[28px] bg-zinc-100 animate-pulse"
        aria-hidden="true"
        data-testid={`${testId}-loading`}
      />
    );
  }

  const temp = Math.round(w.current.temperature_2m);
  const meta = codeMap(w.current.weather_code);
  const windSpeed = w.current.wind_speed_10m ?? 0;
  const min = w.daily?.temperature_2m_min?.[0] != null ? Math.round(w.daily.temperature_2m_min[0]) : null;
  const max = w.daily?.temperature_2m_max?.[0] != null ? Math.round(w.daily.temperature_2m_max[0]) : null;

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      data-testid={testId}
      className="relative overflow-hidden rounded-[28px] text-white shadow-xl shadow-black/25 ring-1 ring-inset ring-white/20 active:shadow-md transition-shadow flex flex-col items-center justify-end text-center col-span-1 aspect-square p-4"
      style={{ background: meta.gradient }}
    >
      {/* Soft radial glow */}
      <span aria-hidden className="absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-[0.18]"
        style={{ background: "radial-gradient(circle at center, #ffffff 0%, transparent 60%)" }} />
      <span aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 55%)" }} />

      {/* LIVE badge — pulsing red dot, top-left */}
      <div className="absolute top-2 left-2.5 z-20 flex items-center gap-1" data-testid={`${testId}-live`}>
        <motion.span
          className="w-2 h-2 rounded-full bg-red-500"
          style={{ boxShadow: "0 0 6px rgba(239,68,68,0.9)" }}
          animate={{ opacity: [1, 0.25, 1], scale: [1, 0.8, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span className="text-[10px] font-black tracking-wider text-white/95 leading-none lowercase">live</span>
      </div>

      {/* Big animated flat weather icon — ~2x larger, absolutely centered so it
          fills the upper area without pushing the temperature block. */}
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="absolute inset-x-0 top-0 bottom-[44px] flex items-center justify-center pointer-events-none"
      >
        <AnimatedWeatherIcon
          code={w.current.weather_code}
          windSpeed={windSpeed}
          size={132}
          testId={`${testId}-icon`}
        />
      </motion.div>

      {/* Temperature + forecast CTA */}
      <div className="relative w-full z-10">
        <div className="flex items-baseline justify-center gap-1 leading-none">
          <span className="text-[28px] font-black tracking-tight">{temp}</span>
          <span className="text-[13px] font-black opacity-90 -ml-0.5">°</span>
          {min != null && max != null && (
            <span className="text-white/80 font-medium text-[10.5px] ml-1.5 leading-none">{max}°/{min}°</span>
          )}
        </div>
        <p className="text-white/95 font-black text-[10.5px] uppercase tracking-wider leading-snug mt-1">
          {t.tap} →
        </p>
      </div>
    </motion.button>
  );
}
